import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSearchSuggestions, ISearchSuggestion } from 'API/Search';
import {
  getActivities,
  getFileSource,
  IActivityCategory,
} from 'API/ActivitiesLobby/activitiesLobby';
import { ACTIVITY_TYPE } from 'Enum/ActivitiesLobby';
import { downloadFile } from 'Utils';
import {
  eshetToursId,
  flyingCarpetId,
} from 'Modules/ActivitiesLobby/Activities/ActivityItem';
import { getSupplierRedirectCode } from 'API/Treatment/treatment';

export interface IResult {
  label: string;
  description?: string;
  indexType?: number;
  onClick?: () => void;
}

export interface IRelatedAction {
  title: string;
  link: string;
  id: number;
  activityTypeId: ACTIVITY_TYPE;
}

const MIN_SEARCH_LENGTH = 2;
const MAX_SEARCH_RESULTS = 5;
const MAX_RELATED_ACTIONS = 4;
const DEBOUNCE_DELAY = 300;

interface UseSearchLogicProps {
  initialOpen?: boolean;
  onClose?: () => void;
  isDesktop: boolean;
}

export const useSearchLogic = ({
  initialOpen = false,
  onClose,
}: UseSearchLogicProps) => {
  const [isActive, setIsActive] = useState(initialOpen);
  const [value, setValue] = useState('');
  const [results, setResults] = useState<IResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activitiesList, setActivitiesList] = useState<IActivityCategory[]>([]);
  const [relatedActions, setRelatedActions] = useState<IRelatedAction[]>([]);

  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchBarRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      const activities = await getActivities();
      if (activities) {
        setActivitiesList(activities);
      }
    };
    fetchActivities();
  }, []);

  const onActivityClick = async (action: IRelatedAction) => {
    switch (action.activityTypeId) {
      case ACTIVITY_TYPE.FILE:
        const fileSource = await getFileSource(action.link);
        if (fileSource) {
          downloadFile(fileSource, '.pdf', action.title || '', false);
        }
        break;

      case ACTIVITY_TYPE.INTERNAL:
        setIsActive(false);
        setValue('');
        setResults([]);
        setRelatedActions([]);
        onClose?.();
        navigate(action.link);
        break;

      case ACTIVITY_TYPE.EXTERNAL:
        if (action.id === flyingCarpetId || action.id === eshetToursId) {
          const code = await getSupplierRedirectCode(
            action.id === flyingCarpetId ? 1 : 2,
          );
          if (action.id === flyingCarpetId) {
            window.open(
              `https://flying-shikum.co.il/?code=${encodeURIComponent(code)}`,
              '_blank',
            );
          } else {
            window.open(
              `https://eshetapollodt.azurewebsites.net/?segmentId=18&subSegmentId=1&auth=${encodeURIComponent(
                code,
              )}`,
              '_blank',
            );
          }
        } else {
          window.open(action.link, '_blank');
        }
        break;
    }
  };

  const getRelatedActions = useCallback(
    (searchValue: string, maxActions: number) => {
      if (
        searchValue.length < MIN_SEARCH_LENGTH ||
        activitiesList.length === 0
      ) {
        return [];
      }

      const actions: IRelatedAction[] = [];

      activitiesList.forEach((activity) => {
        activity.activities.forEach((item) => {
          const wordsToCheck = [
            ...(item.searchWords?.map((word) => word.text) || []),
            item.label,
          ];

          const shouldInclude = wordsToCheck.some((word) =>
            word.toLowerCase().includes(searchValue.toLowerCase()),
          );

          if (shouldInclude && actions.length < maxActions) {
            actions.push({
              title: item.label,
              link: item.link,
              id: item.id,
              activityTypeId: item.activityTypeId,
            });
          }
        });
      });

      return actions;
    },
    [activitiesList],
  );

  const navigateToSearchResults = useCallback(
    (query: string) => {
      navigate(`/search-results?query=${encodeURIComponent(query)}`);
      setIsActive(false);
      setValue('');
      setResults([]);
      setRelatedActions([]);
      onClose?.();
    },
    [navigate, onClose],
  );

  const fetchSuggestions = useCallback(
    async (query: string) => {
      if (query.length < MIN_SEARCH_LENGTH) {
        setResults([]);
        setRelatedActions([]);
        return;
      }

      setIsLoading(true);
      const suggestions = await getSearchSuggestions(query);
      setIsLoading(false);

      if (suggestions) {
        const mappedResults: IResult[] = suggestions
          .slice(0, MAX_SEARCH_RESULTS)
          .map((suggestion: ISearchSuggestion) => ({
            label: suggestion.typeSuggestionText,
            description: suggestion.suggestionText,
            indexType: suggestion.indexType,
            onClick: () => navigateToSearchResults(suggestion.suggestionText),
          }));
        setResults(mappedResults);
      } else {
        setResults([]);
      }

      setRelatedActions(getRelatedActions(query, MAX_RELATED_ACTIONS));
    },
    [navigateToSearchResults, getRelatedActions],
  );

  useEffect(() => {
    if (initialOpen) {
      setIsActive(true);
    }
  }, [initialOpen]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setRelatedActions([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, fetchSuggestions]);

  const onCloseSearch = useCallback(() => {
    setIsActive(false);
    setValue('');
    setResults([]);
    setRelatedActions([]);
    onClose?.();
    setTimeout(() => {
      if (searchBarRef.current) searchBarRef.current.focus();
    }, 0);
  }, [onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCloseSearch();
    } else if (e.key === 'Enter' && value.length >= MIN_SEARCH_LENGTH) {
      navigateToSearchResults(value);
    }
  };

  const clearValue = () => setValue('');

  const showNoResults =
    value.length >= MIN_SEARCH_LENGTH && results.length === 0 && !isLoading;
  const showResults = value.length >= MIN_SEARCH_LENGTH && results.length > 0;
  const hasContent =
    isLoading || showNoResults || showResults || relatedActions.length > 0;

  return {
    isActive,
    setIsActive,
    value,
    setValue,
    results,
    isLoading,
    relatedActions,
    inputRef,
    searchBarRef,
    containerRef,
    onActivityClick,
    onCloseSearch,
    handleInputChange,
    handleKeyDown,
    clearValue,
    showNoResults,
    showResults,
    hasContent,
    MIN_SEARCH_LENGTH,
  };
};
